import {Injectable, Signal, signal} from "@angular/core";
import {BehaviorSubject, catchError, concatMap, defer, EMPTY, finalize, firstValueFrom, fromEvent, interval, Observable, race, retry, share, Subject, Subscription, tap, throwError, timer} from "rxjs";
import {HttpErrorResponse, HttpHeaders} from "@angular/common/http";
import {EntitetType, LydopptakId, MøtebehandlingOpptakValideringsfeil, MøteModell, MøtetranskripsjonRessursClient, SkjemalåsRessursClient, SkjemalåsSkjemaId, SnakketSpråk, ZonedDateTimeSerialisert} from "felles-ng2/tjenester/ressurser/generertNg2HttpKlient";
import {SkjemalåsErrorRespons, Skjemalåshåndtering, SkjemalåsOkRespons} from "felles-ng2/tjenester/Skjemalåshåndtering";
import {somEntitetReferanseSpesifikasjon} from "felles-ng2/modell/entitetReferanseSpesifikasjonHjelpere";
import {Appkonfigurasjon} from "felles-ng2/tjenester/Appkonfigurasjon";
import {initDebugger} from "felles-ng2/støttefunksjoner/debug";
import {erIkkeNil, erNil} from "felles-ng2/støttefunksjoner/nilsjekk";
import {Lydfragment, Mikrofonopptak, MikrofonopptakFabrikk, MikrofonopptakInnstillinger, Mikrofonstatus} from "felles-ng2/tjenester/Mikrofonopptak";
import {Temporal} from "temporal-polyfill";
import {promiseMedResolvere} from "felles-ng2/støttefunksjoner/promiser";
import {lesDurationSerialisertOpt, skrivDurationSerialisert, tomDuration} from "felles-ng2/støttefunksjoner/temporal";
import {Dvalesperre} from "felles-ng2/tjenester/Dvalesperre";
import {erValideringsfeilExceptionMedFeilkode, finnValideringsfeilMedFeilkode} from "felles-ng2/infrastruktur/http/httpErrorResponseTyper";
import {HttpResponskoder} from "felles-ng2/infrastruktur/http/httpResponskoder";
import Duration = Temporal.Duration;
import {Klokke} from "felles-ng2/tjenester/Klokke";

const debug = initDebugger('MøteTranskripsjon');

/**
 * Tar opp lyden fra et møte og sender lydfilene til server for transkripsjon.
 *
 * Tjenesten er en singleton og eier ett møteopptak av gangen, uavhengig av hvilken komponent som er montert.
 * Selve mikrofonhåndteringen og oppdelingen i selvstendige lydfiler ligger i {@link Mikrofonopptak};
 * denne tjenesten legger på møtekonteksten: skjemalås og opptak på server.
 *
 * Merk at ett møteopptak gir flere `Lydopptak` på server: hver lydfil blir sitt eget lydopptak.
 * Serveren transkriberer ett lydopptak av gangen, så
 * hver lydfil avsluttes så snart den er lastet opp. Oppsummering av møtet går på tvers av alle
 * lydopptakene som er registrert på møtet.
 */
@Injectable()
export class MøteTranskripsjon {

    /** Språket det snakkes i møtet når ikke noe annet er valgt, tilsvarer I18nStøttedeSnakkedeSpråk.standard_språk på server */
    static readonly standardSnakketSpråk = SnakketSpråk.norsk;

    private _skjemalåsOkRespons = signal<SkjemalåsOkRespons | null>(null);
    private _skjemalåsSistOppdatert = signal<ZonedDateTimeSerialisert | null>(null);
    private _skjemalåsErrorRespons = signal<SkjemalåsErrorRespons | null>(null);

    private readonly opptakPågår = signal(false);

    /** Sann mens et opptak går. Signal slik at komponenter kan vise seg selv når transkripsjonen er i gang */
    readonly erIGang: Signal<boolean> = this.opptakPågår.asReadonly();

    private readonly opptakPauset = signal(false);

    /** Sann mens opptaket står i pause. Signal slik at gui kan vise om det pågår eller er pauset */
    readonly erPauset: Signal<boolean> = this.opptakPauset.asReadonly();

    private readonly utenLyd = signal(false);

    /**
     * Sann når opptaket går, men mikrofonen ikke har fanget opp lyd de siste {@link ingenLydVarselEtterMs}.
     * Tyder på at feil mikrofon er valgt, eller at den er dempet. Er alltid usann mens opptaket står i pause.
     */
    readonly ingenLydRegistrert: Signal<boolean> = this.utenLyd.asReadonly();

    /** Hvor lenge (ms) mikrofonen kan være uten lyd før {@link ingenLydRegistrert} slår til. Kan overstyres av tester og storyer */
    ingenLydVarselEtterMs = 30 * 1000;

    /** Tidspunktet (epoch ms) mikrofonen sist fanget opp lyd, eller opptaket startet/fortsatte */
    private sisteLydMs = 0;

    private readonly _mikrofonstatus = signal<Mikrofonstatus>('tilkoblet');

    /** Tilstanden til mikrofonen mens opptaket går, se {@link Mikrofonopptak.mikrofonstatus}. Er `tilkoblet` når det ikke tas opp */
    readonly mikrofonstatus: Signal<Mikrofonstatus> = this._mikrofonstatus.asReadonly();

    private readonly _ventendeLydfiler = signal(0);

    /** Lydfiler som er tatt opp, men ennå ikke lastet opp til server */
    readonly ventendeLydfiler: Signal<number> = this._ventendeLydfiler.asReadonly();

    private readonly _opplastingFeiler = signal(false);

    /**
     * Sann når siste forsøk på å laste opp en lydfil feilet, og den forsøkes lastet opp på nytt.
     * Opptaket fortsetter imens, og lydfilene venter i {@link ventendeLydfiler}.
     */
    readonly opplastingFeiler: Signal<boolean> = this._opplastingFeiler.asReadonly();

    private readonly _avbrudd = signal<MøteTranskripsjonFeil | null>(null);

    /**
     * Feilen som avbrøt opptaket uten at bruker stoppet det, feks fordi serveren avviste en lydfil.
     * Opptaket er da stoppet og ryddet opp etter, men feilen beholdes til neste {@link start} eller
     * {@link kvitterAvbrudd}, slik at gui-en kan forklare bruker hva som skjedde.
     */
    readonly avbrudd: Signal<MøteTranskripsjonFeil | null> = this._avbrudd.asReadonly();

    /** Kan overstyres av tester, slik at de slipper å vente på virkelige ventetider */
    opplastingsinnstillinger: Opplastingsinnstillinger = {
        førsteVentetidMs: 1000,
        maksVentetidMs: 30 * 1000,
        forsøkEtterStopp: 3
    };

    /** Sann fra {@link stopp} er kalt til opptaket er ryddet, da får opplastingene et begrenset antall nye forsøk */
    private stoppes = false;

    /** Sann når mikrofonen er i gang, se {@link Mikrofonopptak.vedOpptaksstart} */
    private opptakStartet = false;

    /** Feilen mikrofonopptaket stoppet med underveis, se {@link holdTilbakeMikrofonfeil} */
    private mikrofonfeil?: { årsak: unknown };

    /** Ber opplastinger som venter på et nytt forsøk om å forsøke med en gang, feks når nettet er tilbake */
    private readonly forsøkOpplastingPåNyttNå = new Subject<void>();

    private readonly _møte = signal<MøteModell | null>(null);

    /**
     * Møtet det tas opptak av, null før opptak er startet. Beholdes etter at
     * opptaket er stoppet, se {@link rydd}, slik at gui kan vise møtet opptakene hører til.
     */
    readonly møte: Signal<MøteModell | null> = this._møte.asReadonly();

    private readonly lydnivået = new BehaviorSubject<number>(0);

    private readonly varigheten = new BehaviorSubject<Duration>(tomDuration());

    private opptak?: Mikrofonopptak;

    /**
     * Innstillinger for mikrofonopptaket, utover mikrofonen {@link start} får oppgitt.
     * Standardverdiene i {@link Mikrofonopptak} gjelder for det som ikke settes her – ment for at
     * tester kan kjøre med kortere lydfiler enn et virkelig møte.
     */
    mikrofoninnstillinger: Partial<MikrofonopptakInnstillinger> = {};

    /**
     * Hvor ofte skjemalåsen oppdateres mens opptaket går, se {@link Skjemalåshåndtering}. Ment for at
     * tester og storyer kan vise at låsen blir overtatt uten å vente et helt intervall.
     */
    skjemalåsOppdateringsintervallMs = 15 * 1000;

    /**
     * Mikrofonen {@link start} sist ble kalt med. Beholdes etter at opptaket er stoppet, slik at et
     * nytt opptak av samme møte kan tas opp med samme mikrofon uten å spørre bruker igjen.
     */
    private mikrofonDeviceId: string | null = null;

    /** Språket {@link start} sist ble kalt med, beholdes av samme grunn som {@link mikrofonDeviceId} */
    private språk: SnakketSpråk = MøteTranskripsjon.standardSnakketSpråk;

    /** Brukes for å terminere ulike subscriptions som har blitt startet */
    private vedTerminering?: Subscription;

    /** Skjemalås som sikrer at ikke flere opptak startes for samme møte samtidig */
    private skjemalås?: Skjemalåshåndtering<Transkripsjonslås>;

    /**
     * Lydopptakene som er opprettet på server så langt, i opptaksrekkefølge. Beholdes etter at opptaket
     * er stoppet slik at møtet kan oppsummeres i etterkant, og nullstilles først ved neste {@link start}.
     */
    private opptakIder: LydopptakId[] = [];

    /** Løftet som fullfører når alle leverte lydfiler er lastet opp */
    private opplastingerFerdig?: Promise<void>;

    /**
     * Lydnivå, på samme skala som {@link MøteTranskripsjon.lydnivå}, som regnes som at mikrofonen fanger opp lyd.
     * Tilsvarer omtrent stillhetsterskelen i {@link Mikrofonopptak} – tale gir godt utslag over dette, mens en
     * mikrofon som er dempet eller ikke fanger opp rommet ligger under.
     */
    private readonly lydnivåTerskel = 0.01;

    constructor(private møtetranskripsjonRessurs: MøtetranskripsjonRessursClient,
                private skjemalåsRessurs: SkjemalåsRessursClient,
                private konfigurasjon: Appkonfigurasjon,
                private mikrofonopptakFabrikk: MikrofonopptakFabrikk,
                private dvalesperre: Dvalesperre,
                private klokke: Klokke) {
    }

    get skjemalåsSistOppdatert(): Signal<ZonedDateTimeSerialisert | null> {
        return this._skjemalåsSistOppdatert.asReadonly();
    }

    get skjemalåsOkRespons(): Signal<SkjemalåsOkRespons | null> {
        return this._skjemalåsOkRespons.asReadonly();
    }

    get skjemalåsErrorRespons(): Signal<SkjemalåsErrorRespons | null> {
        return this._skjemalåsErrorRespons.asReadonly();
    }

    /**
     * Lydopptaket som sist ble lastet opp, eller undefined før den første lydfila er levert.
     */
    get sisteOpptakId(): LydopptakId | undefined {
        return this.opptakIder[this.opptakIder.length - 1];
    }

    /** Lydopptakene dette møteopptaket har lastet opp, i opptaksrekkefølge */
    get alleOpptakIder(): readonly LydopptakId[] {
        return this.opptakIder;
    }

    /**
     * Mikrofonen det sist ble startet opptak med, null når standardmikrofonen ble brukt eller ingen
     * transkripsjon er startet ennå. Ment for å fortsette et stoppet opptak med samme mikrofon.
     */
    get sistBrukteMikrofon(): string | null {
        return this.mikrofonDeviceId;
    }

    /** Språket det sist ble startet opptak med, ment for å fortsette et stoppet opptak med samme språk */
    get sistBrukteSpråk(): SnakketSpråk {
        return this.språk;
    }

    /** Målt lydnivå fra 0 til 1 mens opptaket går. Delegerer til {@link Mikrofonopptak.lydnivå} */
    lydnivå(): Observable<number> {
        return this.lydnivået.asObservable();
    }

    /**
     * Hvor lenge opptaket har vart, målt fra start og uten tiden det har stått i pause.
     * Oppdateres hvert sekund mens opptaket går. Delegerer til {@link Mikrofonopptak.varighet}
     */
    varighet(): Observable<Duration> {
        return this.varigheten.asObservable();
    }

    /**
     * Starter opptak av møtet.
     *
     * Skjemalåsen tas først, slik at vi feiler raskt dersom en annen bruker allerede tar opp møtet,
     * og deretter startes mikrofonen. Feiler mikrofonen frigis låsen igjen.
     *
     * Merk at det ikke opprettes noe på server her – et lydopptak opprettes først når det finnes en
     * lydfil å opprette det av, se {@link lastOppLydfil}.
     *
     * @param overtaEksisterendeLås sann dersom en eksisterende skjemalås (f.eks. fra en annen bruker) skal overtas
     * @param språk språket det snakkes i møtet, sendes med hver lydfil slik at transkripsjonen gjøres på riktig språk
     */
    async start(møte: MøteModell, deviceId: string | null, overtaEksisterendeLås = false, språk: SnakketSpråk = MøteTranskripsjon.standardSnakketSpråk): Promise<void> {
        if (this.erIGang()) throw new MøteTranskripsjonFeil('Det pågår allerede et opptak', 'pågåendeOpptak');

        this._møte.set(møte);
        this.mikrofonDeviceId = deviceId;
        this.språk = språk;
        this.opptakIder = [];
        this._avbrudd.set(null);
        this.vedTerminering = new Subscription();
        //låsesignalene gjelder opptaket som startes nå, så en feil fra et tidligere forsøk skal ikke bli hengende igjen
        this.nullstillSkjemalåssignaler();

        //dersom denne feiler kastes en exception av typen SkjemalåsErrorRespons
        //merk at SkjemalåsErrorRespons også blir eksponert via skjemalåsErrorRespons
        await this.opprettLåsForMøte(overtaEksisterendeLås);

        try {
            await this.startMikrofonopptak(deviceId);
        } catch (feil) {
            await this.ryddOppEtterMislykketStart();
            throw new MøteTranskripsjonFeil(feil, 'startOpptak');
        }

        //et møte varer lenge, uten sperren slukkes skjermen og maskinen går i dvale midt i opptaket
        void this.dvalesperre.aktiver();
    }

    /** Pauser opptaket. Lydfilen som tas opp nå avsluttes og leveres */
    pause(): void {
        if (erNil(this.opptak)) return;

        this.opptak.pause();
        this.opptakPauset.set(true);
        this.utenLyd.set(false);
    }

    /** Fortsetter et pauset opptak */
    fortsett(): void {
        if (erNil(this.opptak)) return;

        this.opptak.fortsett();
        this.opptakPauset.set(false);
        //et pauset opptak fanger ingen lyd, så tiden uten lyd måles først fra nå
        this.sisteLydMs = this.klokke.nåSomEpochMillisekunder();
    }

    /**
     * Stopper opptaket: siste lydfil leveres og lastes opp, og skjemalåsen frigis. Låsen frigis også
     * dersom opplastingen feiler.
     *
     * Hvert lydopptak avsluttes og transkriberes fortløpende ved opplasting, se {@link lastOppLydfil},
     * så her venter vi bare på at de utestående opplastingene blir ferdige. Lydfiler som venter på et
     * nytt forsøk forsøkes med en gang, og får et begrenset antall forsøk, se
     * {@link Opplastingsinnstillinger.forsøkEtterStopp}. Lykkes de ikke, feiler stoppen med en
     * {@link MøteTranskripsjonFeil} av typen `opplasting`.
     */
    async stopp(): Promise<void> {
        if (erNil(this.opptak)) return;

        this.stoppes = true;
        this.forsøkOpplastingPåNyttNå.next();
        try {
            //stopp fullfører fragmentstrømmen etter at siste lydfil er levert
            this.opptak.stopp();
            await this.opplastingerFerdig;
        } finally {
            await this.rydd();
        }
    }

    /** Glemmer {@link avbrudd}, når bruker har sett hvorfor opptaket stoppet */
    kvitterAvbrudd(): void {
        this._avbrudd.set(null);
    }

    //private hjelpemetoder

    /** Oppretter lås for møte, returnerer promise som resolver når initielt 'opprett lås' kall har returnert'.
     * Vil periodisk oppdatere låsen. */
    private async opprettLåsForMøte(overtaEksisterendeLås: boolean): Promise<SkjemalåsOkRespons> {
        this.skjemalås = new Skjemalåshåndtering<Transkripsjonslås>(
          /** for at opprettelse av lås skal feile dersom annen bruker allerede har startet opptak må vi sikre at skjemaspesifikasjon inneholder unike verdier */
          () => ({opptakIder: [...this.opptakIder], ts: new Date().getTime()}),
          SkjemalåsSkjemaId.moetetranskripsjon,
          this.konfigurasjon.klientId,
          this.skjemalåsRessurs,
          this.skjemalåsOppdateringsintervallMs);

        //oppretter lås og returnerer promise som resolver når initielt 'opprett lås' kall til server har returnert
        const låsopprettelse = this.skjemalås.opprettSkjemalåsForsinket(
          this._møte()!.eiendeGruppeId,
          somEntitetReferanseSpesifikasjon(EntitetType.møte, this._møte()!.id),
          overtaEksisterendeLås,
          0)
          //share() slik at vi kan opprette flere subscriptions uten at det opprettes multiple låser
          .pipe(share());

        this.vedTerminering!.add(
          låsopprettelse
            .subscribe({
                next: skjemalåsrespons => {
                    this._skjemalåsOkRespons.set(skjemalåsrespons);
                    this._skjemalåsSistOppdatert.set(skjemalåsrespons.laas.oppdatertDato);
                },
                error: feil => {
                    if (feil instanceof SkjemalåsErrorRespons)
                        this._skjemalåsErrorRespons.set(feil);
                }
            }));

        return firstValueFrom(låsopprettelse);
    }

    private startMikrofonopptak(deviceId: string | null): Promise<void> {
        this.opptak = this.mikrofonopptakFabrikk.opprett({...this.mikrofoninnstillinger, deviceId: deviceId ?? undefined});
        this.vedTerminering!.add(this.opptak.lydnivå.subscribe(nivå => this.vedLydnivå(nivå)));
        //lydnivået meldes ikke nødvendigvis når mikrofonen er død, så tiden uten lyd vurderes også jevnlig
        this.sisteLydMs = this.klokke.nåSomEpochMillisekunder();
        this.vedTerminering!.add(interval(1000).subscribe(() => this.vurderOmLydRegistreres()));
        this.vedTerminering!.add(this.opptak.varighet.subscribe(varighet => this.varigheten.next(varighet)));
        this.vedTerminering!.add(this.opptak.mikrofonstatus.subscribe(status => this.vedMikrofonstatus(status)));
        //lukkes fanen går lyden som ikke er levert som lydfil ennå tapt – opptil en hel lydfil – og
        //lydfiler som venter på opplasting likeså. Nettleseren spør da bruker om fanen likevel skal lukkes.
        this.vedTerminering!.add(fromEvent<BeforeUnloadEvent>(window, 'beforeunload').subscribe(hendelse => this.advarMotLukking(hendelse)));

        const opplastinger = this.opptak.start().pipe(
            catchError(feil => this.holdTilbakeMikrofonfeil(feil)),
            tap(() => this.lydfilVenter()),
            //én lydfil av gangen, i opptaksrekkefølge. Nye forsøk gjelder kun lydfila som feilet – lå retry
            //i den ytre pipe-en, ville en feilet opplasting startet et helt nytt mikrofonopptak
            concatMap(fragment => this.lastOppLydfil(fragment).pipe(
                tap({error: () => this._opplastingFeiler.set(true)}),
                retry({delay: this.nyttForsøkFor(fragment)}),
                tap(opptakId => this.registrerOpplastet(opptakId)),
                finalize(() => this.lydfilErFerdig())
            ))
        );

        this.opptakPågår.set(true);

        const opptakStartetProm = promiseMedResolvere<void>();
        const opplastingerFerdigProm = promiseMedResolvere<void>();
        this.opplastingerFerdig = opplastingerFerdigProm.promise;
        //feilen leses av stopp() når den venter, uten stopp er det avbryt() som tar seg av den
        this.opplastingerFerdig.catch(() => undefined);

        this.vedTerminering!.add(this.opptak.vedOpptaksstart.subscribe(() => {
            this.opptakStartet = true;
            opptakStartetProm.resolve();
        }));

        // strømmen er kald og må abonneres kun én gang, ellers startes opptaket to ganger
        this.vedTerminering!.add(opplastinger.subscribe({
            error: feil => {
                debug('opptak eller opplasting feilet %o', feil);
                const transkripsjonsfeil = feil instanceof MøteTranskripsjonFeil ? feil : new MøteTranskripsjonFeil(feil, 'mikrofon');
                opplastingerFerdigProm.reject(transkripsjonsfeil);

                //før opptaket er i gang er det start() som rydder opp etter feilen
                if (!this.opptakStartet) opptakStartetProm.reject(feil);
                //bruker venter på stoppen, og får feilen derfra
                else if (!this.stoppes) void this.avbryt(transkripsjonsfeil);
            },
            complete: () => {
                opplastingerFerdigProm.resolve();
                //bruker som stopper selv får lydfilene lastet opp, og trenger ingen forklaring
                if (erIkkeNil(this.mikrofonfeil) && !this.stoppes) {
                    debug('mikrofonopptaket feilet underveis %o', this.mikrofonfeil.årsak);
                    void this.avbryt(new MøteTranskripsjonFeil(this.mikrofonfeil.årsak, 'mikrofon'));
                }
            }
        }));

        return opptakStartetProm.promise;
    }

    /**
     * Feiler mikrofonen underveis har den levert lyden så langt først, se Mikrofonopptak.start. Feilen
     * holdes tilbake til de lydfilene er lastet opp – ellers ville concatMap sendt den videre med en gang,
     * og forkastet lydfilene som venter. Før start er det ingenting å laste opp, og feilen går rett videre.
     */
    private holdTilbakeMikrofonfeil(feil: unknown): Observable<never> {
        if (!this.opptakStartet) return throwError(() => feil);
        this.mikrofonfeil = {årsak: feil};
        return EMPTY;
    }

    private lydfilVenter(): void {
        this._ventendeLydfiler.update(antall => antall + 1);
    }

    private lydfilErFerdig(): void {
        this._ventendeLydfiler.update(antall => Math.max(0, antall - 1));
    }

    private registrerOpplastet(opptakId: LydopptakId): void {
        this.opptakIder.push(opptakId);
        this._opplastingFeiler.set(false);
    }

    /**
     * Bestemmer om, og når, en lydfil som ikke kom fram skal forsøkes på nytt. Feil som kan gå over –
     * feks at nettet er borte eller serveren er utilgjengelig – forsøkes på nytt i det uendelige mens
     * opptaket går, og et begrenset antall ganger etter stopp. Andre feil, som at bruker ikke lenger har
     * tilgang til møtet, kan ikke forsøkes bort, og avbryter opptaket.
     *
     * Returnerer en funksjon per lydfil, slik at telleren for forsøk etter stopp gjelder den ene lydfila.
     */
    private nyttForsøkFor(fragment: Lydfragment): (feil: unknown, forsøk: number) => Observable<unknown> {
        let forsøkEtterStopp = 0;
        return (feil, forsøk) => {
            if (!this.kanForsøkesPåNytt(feil)){
                const maksOpptakslengdeOverskredet = finnValideringsfeilMedFeilkode(feil, MøtebehandlingOpptakValideringsfeil.maks_opptakslengde_overskredet)
                if (erIkkeNil(maksOpptakslengdeOverskredet)){
                    return throwError(() => new MøteTranskripsjonFeil(feil, 'maks_opptakslengde_overskredet', lesDurationSerialisertOpt(maksOpptakslengdeOverskredet.data.maksimum)));
                }
                return throwError(() => new MøteTranskripsjonFeil(feil, 'opplasting'));
            }

            if ((this.stoppes && ++forsøkEtterStopp > this.opplastingsinnstillinger.forsøkEtterStopp)) {
                return throwError(() => new MøteTranskripsjonFeil(feil, 'opplasting'));
            }

            const ventetidMs = this.ventetidFørNyttForsøk(forsøk);
            debug(`opplasting av lydfil ${fragment.id} feilet (forsøk ${forsøk}), forsøker igjen om ${ventetidMs} ms %o`, feil);
            return this.nyttForsøkEtter(ventetidMs);
        };
    }

    /**
     * Sann for feil som kan gå over av seg selv: nettet er borte (status 0), tidsavbrudd, for mange
     * forespørsler og feil på server. Øvrige http-feil, som manglende tilgang, får samme svar neste gang.
     * Feil som ikke kommer fra http-laget behandles som forbigående.
     */
    private kanForsøkesPåNytt(feil: unknown): boolean {
        if (erValideringsfeilExceptionMedFeilkode(feil, MøtebehandlingOpptakValideringsfeil.maks_opptakslengde_overskredet)) return false;

        if (!(feil instanceof HttpErrorResponse)) return true;
        return feil.status === 0 || feil.status === HttpResponskoder.REQUEST_TIMEOUT || feil.status === HttpResponskoder.TO_MANY_REQUESTS || feil.status >= HttpResponskoder.INTERNAL_SERVER_ERROR;
    }
    
    /** Dobles for hvert forsøk, opp til maks. Etter stopp skal bruker ikke vente lenger enn nødvendig */
    private ventetidFørNyttForsøk(forsøk: number): number {
        const {førsteVentetidMs, maksVentetidMs} = this.opplastingsinnstillinger;
        return this.stoppes ? førsteVentetidMs : Math.min(maksVentetidMs, førsteVentetidMs * 2 ** (forsøk - 1));
    }

    /** Når ventetiden er ute – eller tidligere, når nettet er tilbake eller bruker har bedt om stopp */
    private nyttForsøkEtter(ventetidMs: number): Observable<unknown> {
        return race(timer(ventetidMs), fromEvent(window, 'online'), this.forsøkOpplastingPåNyttNå);
    }

    /**
     * Opptaket stoppet av seg selv, uten at bruker ba om det. Mikrofonen og låsen frigis som ved en
     * vanlig stopp, og feilen blir stående i {@link avbrudd} slik at gui-en kan forklare hva som skjedde.
     */
    private async avbryt(feil: MøteTranskripsjonFeil): Promise<void> {
        debug('opptaket ble avbrutt %o', feil);
        this._avbrudd.set(feil);
        this.opptak?.stopp();
        await this.rydd();
    }

    /**
     * Ber nettleseren spørre bruker før fanen lukkes eller lastes på nytt. Teksten i spørsmålet bestemmes
     * av nettleseren, og kan ikke tilpasses.
     */
    private advarMotLukking(hendelse: BeforeUnloadEvent): void {
        debug('fanen lukkes mens opptaket går, ber nettleseren om å advare bruker');
        hendelse.preventDefault();
        //eldre nettlesere krever at returnValue settes
        hendelse.returnValue = '';
    }

    private vedMikrofonstatus(status: Mikrofonstatus): void {
        if (status !== this._mikrofonstatus()) debug(`mikrofonen er ${status}`);
        this._mikrofonstatus.set(status);
    }

    /**
     * Laster opp én lydfil til møtet. Serveren transkriberer lydopptaket så snart det er lastet opp.
     *
     * Opprettelsen merker lydopptaket med hvor lenge fila varer – varigheten måles under opptaket, se {@link Lydfragment.varighet}, og er dermed kun
     * kjent her på klienten. defer sørger for at hvert nytt forsøk gjør et nytt kall, se {@link nyttForsøkFor}.
     * Avsluttes abonnementet, avbrytes også kallet.
     */
    private lastOppLydfil(fragment: Lydfragment): Observable<LydopptakId> {
        return defer(() => {
            debug(`laster opp lydfil ${fragment.id} (${fragment.lyddata.size} bytes, ${fragment.varighet.toString()})`);
            return this.møtetranskripsjonRessurs.nyttOpptak(
              this._møte()!.id,
              fragment.lyddata,
              {varighet: skrivDurationSerialisert(fragment.varighet), spraak: this.språk},
              {headere: new HttpHeaders({'Content-Type': fragment.lyddata.type || 'application/octet-stream'})}
            //feilen vises ikke for hvert forsøk, gui-en viser i stedet at opplastingen feiler, se opplastingFeiler
            ).somObservable();
        });
    }

    private vedLydnivå(nivå: number): void {
        this.lydnivået.next(nivå);
        if (nivå >= this.lydnivåTerskel) this.sisteLydMs = this.klokke.nåSomEpochMillisekunder();
        this.vurderOmLydRegistreres();
    }

    private vurderOmLydRegistreres(): void {
        const utenLyd = this.erIGang() && !this.erPauset() && this.klokke.nåSomEpochMillisekunder() - this.sisteLydMs >= this.ingenLydVarselEtterMs;
        if (utenLyd !== this.utenLyd()) debug(utenLyd ? 'mikrofonen fanger ikke opp lyd' : 'mikrofonen fanger opp lyd igjen');
        this.utenLyd.set(utenLyd);
    }

    /** Mikrofonen kom aldri i gang, så ingen lydfil er lastet opp og det finnes ingenting å rulle tilbake på server */
    private async ryddOppEtterMislykketStart(): Promise<void> {
        await this.rydd();
    }

    private async rydd(): Promise<void> {
        this.vedTerminering?.unsubscribe();
        this.vedTerminering = undefined;
        this.opptak = undefined;
        this.opptakPågår.set(false);
        this.opptakPauset.set(false);
        this.utenLyd.set(false);
        this._mikrofonstatus.set('tilkoblet');
        this._opplastingFeiler.set(false);
        this._ventendeLydfiler.set(0);
        this.stoppes = false;
        this.opptakStartet = false;
        this.mikrofonfeil = undefined;
        this.opplastingerFerdig = undefined;
        //møtet og opptakIder beholdes med vilje, slik at møtet kan oppsummeres etter at opptaket er stoppet
        this.lydnivået.next(0);
        this.varigheten.next(tomDuration());

        void this.dvalesperre.deaktiver();

        await this.skjemalås?.frigiSkjemalås();
        this.skjemalås = undefined;
    }

    /** Glemmer tilstanden til låsen fra forrige opptak, slik at gui-en kun viser låsen dette opptaket har */
    private nullstillSkjemalåssignaler(): void {
        this._skjemalåsOkRespons.set(null);
        this._skjemalåsSistOppdatert.set(null);
        this._skjemalåsErrorRespons.set(null);
    }
}


/** Hvordan opplastingen av lydfilene forsøkes på nytt når den feiler, se {@link MøteTranskripsjon.opplastingsinnstillinger} */
export interface Opplastingsinnstillinger {
    /** Ventetiden (ms) før første nye forsøk. Dobles for hvert forsøk, opp til {@link maksVentetidMs} */
    førsteVentetidMs: number;
    maksVentetidMs: number;
    /**
     * Hvor mange nye forsøk en lydfil får etter at opptaket er bedt stoppet. Mens opptaket går forsøkes
     * det i det uendelige – det er bedre at lydfilene venter enn at de går tapt – men en stopp skal
     * ikke kunne henge for alltid.
     */
    forsøkEtterStopp: number;
}

/**
 * Feil ved opptak av møtet:
 * - `pågåendeOpptak` – det går allerede et opptak
 * - `startOpptak` – mikrofonen kom ikke i gang
 * - `opplasting` – en lydfil kunne ikke lastes opp, se {@link MøteTranskripsjon.opplastingFeiler}
 * - `mikrofon` – mikrofonopptaket feilet underveis
 * - `maks_opptakslengde_overskredet` – opptaket ble avbrutt fordi det var for langt (i dette tilfellet vil data være satt til maksimal lengde som Duration)
 */
export class MøteTranskripsjonFeil extends Error {
    constructor(public readonly årsak: any, public readonly type: 'pågåendeOpptak' | 'startOpptak' | 'opplasting' | 'mikrofon' | 'maks_opptakslengde_overskredet', public data?: any) {
        super(årsak?.message ?? String(årsak));
    }
}

/**
 * Skjemaet som lagres i skjemalåsen. Transkripsjonen har ikke noe skjema å gjenopprette, låsen brukes
 * kun til å hindre at to brukere tar opp samme møte samtidig – innholdet er derfor bare sporingsinfo.
 */
export interface Transkripsjonslås {
    /** Lydopptakene som er opprettet så langt i dette møteopptaket, i den rekkefølgen de ble tatt opp */
    opptakIder: LydopptakId[];
    /** Tidsstempel som sørger for at transkripsjonslåsen oppfattes som endret ved hver fornyelse av låsen (som dermed sørger for at låsen fornyes mot server) */
    ts: number;
}
